import { useAuth } from '../../utils/AuthProvider';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuLabel, 
    DropdownMenuSeparator, 
    DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { LogOut, Menu, UserCircle, Settings, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';

interface IHeaderProps {
	showSideBar: () => void;
}
const Header = (props: IHeaderProps) => {
	const { account, logout } = useAuth();
	const { showSideBar } = props;
    const location = useLocation();

    const pageLabel = location.pathname
        .split('/')
        .filter(Boolean)
        .map((part) => part.replace(/-/g, ' '))
        .join(' / ') || 'dashboard';

	return (
		<header className={cn(
            "sticky top-0 z-30 flex h-16 w-full items-center justify-between",
            "border-b border-border/70 bg-background/75 backdrop-blur-xl supports-[backdrop-filter]:bg-background/65",
            "px-4 sm:px-6 lg:px-8"
		)}>
			<div className="flex items-center gap-4">
                <Button 
                	variant="ghost" 
                	size="icon" 
                    	className="lg:hidden rounded-xl" 
                	onClick={() => showSideBar()}
                	title="Toggle Sidebar"
                >
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Toggle Sidebar</span>
                </Button>

                <div className="hidden lg:flex flex-col">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">CMS panel</p>
                    <p className="text-sm font-semibold text-foreground/90 capitalize">{pageLabel}</p>
                </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
                <Button 
                	variant="ghost" 
                	size="icon"
                	title="Notifications"
                    	className="relative rounded-xl border border-transparent bg-background/40 hover:bg-primary/5 hover:text-primary"
                >
                    <Bell className="h-5 w-5" />
                    <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-orange-500"></span>
                </Button>

                {account && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button 
                            	variant="ghost" 
                            	className={cn(
                            		"relative h-10 flex items-center gap-2",
                                		"rounded-xl border border-transparent px-2 transition-colors lg:px-3",
                                		"bg-background/55 hover:border-primary/20 hover:bg-primary/5"
                            	)}
                            >
                                <Avatar className="h-8 w-8">
                                    <AvatarImage src={account.avatar} alt={account.fullName} />
                                    <AvatarFallback>
                                    	<UserCircle className="h-5 w-5" />
                                    </AvatarFallback>
                                </Avatar>
                                <div className="hidden flex-col items-start sm:flex">
                                    <span className="text-sm font-medium leading-none">
                                    	{account.firstName || account.fullName}
                                    </span>
                                    <span className="text-xs text-muted-foreground">Admin</span>
                                </div>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56" align="end" forceMount>
                            <DropdownMenuLabel className="font-normal">
                                <div className="flex flex-col space-y-1">
                                    <p className="text-sm font-medium leading-none">
                                    	{account.fullName}
                                    </p>
                                    <p className="text-xs leading-none text-muted-foreground">
                                        {account.username}
                                    </p>
                                </div>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="cursor-pointer" disabled>
                                <Settings className="mr-2 h-4 w-4" />
                                <span>Cài đặt</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                            	className={cn(
                            		"cursor-pointer",
                            		"text-destructive focus:text-destructive focus:bg-destructive/10"
                            	)} 
                            	onClick={() => logout()}
                            >
                                <LogOut className="mr-2 h-4 w-4" />
                                <span>Đăng xuất</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>
		</header>
	);
};

export default Header;
